import { useState, useEffect } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faClock } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { ErrorCode, PickupLocationType } from '../../../__generated__/graphql';
import LoadingSpinner from '../../components/LoadingSpinner';
import {
  useGetOperatingHoursByLocationAndTypeQuery,
  useCreateOperatingHoursMutation,
  useUpdateOperatingHoursMutation,
  GetOperatingHoursByLocationAndTypeDocument,
} from '../../services/operatingHours/__generated__/queries.generated';
import OperatingHoursFormModal from './components/OperatingHoursFormModal';
import CurrentHoursList from './components/CurrentHoursList';

const Container = styled.div`
  ${tw`max-w-screen-xl mx-auto p-6 bg-white rounded-lg shadow-card my-8 mb-16 w-full box-border`}
`;

const Header = styled.div`
  ${tw`flex justify-between items-center mb-6 pb-4 border-b border-border-default`}
`;

const Title = styled.h1`
  ${tw`text-2xl font-bold text-text-primary`}
`;

const CloseButton = styled.button`
  ${tw`text-text-secondary focus:outline-none transition-colors duration-200`}
`;

const Description = styled.p`
  ${tw`text-text-secondary mb-6 text-center`}
`;

const ButtonContainer = styled.div`
  ${tw`flex justify-end gap-4 mt-8`}
`;

const Button = styled.button`
  ${tw`px-6 py-2 rounded-md font-medium focus:outline-none transition-colors duration-200 bg-black text-white max-w-full min-w-0 box-border`}
  &:hover {
    ${tw`bg-blue-700`}
  }
`;

const CancelButton = styled(Button)`
  ${tw`bg-bg-secondary text-text-primary`}
  &:hover {
    ${tw`bg-gray-300`}
  }
`;

const LoadingContainer = styled.div`
  ${tw`flex justify-center items-center py-20`}
`;

const ErrorContainer = styled.div`
  ${tw`bg-red-50 text-red-700 p-4 rounded-lg mb-6 flex items-center`}
`;

const SuccessBanner = styled.div`
  ${tw`bg-green-50 text-green-700 p-4 rounded-lg mb-6 flex items-center justify-between`}
`;

const TimesIcon = styled(FontAwesomeIcon)`
  ${tw`mr-2`}
`;

const CreateOperatingHoursButtonContainer = styled.div`
  ${tw`flex justify-center items-center mt-6`}
`;

// New styled components for displaying current hours
const CurrentHoursContainer = styled.div`
  ${tw`mt-6 border border-border-default rounded-lg overflow-hidden`}
`;

const CurrentHoursHeader = styled.div`
  ${tw`bg-bg-secondary p-4 border-b border-border-default flex justify-between items-center`}
`;

const CurrentHoursTitle = styled.h2`
  ${tw`text-lg font-medium text-text-primary flex items-center`}
`;

const ClockIcon = styled(FontAwesomeIcon)`
  ${tw`mr-2 text-text-tertiary`}
`;

// Generate time options in 30-minute increments
const generateTimeOptions = () => {
  const options = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute of [0, 30]) {
      const hourFormatted = hour.toString().padStart(2, '0');
      const minuteFormatted = minute.toString().padStart(2, '0');
      const time = `${hourFormatted}:${minuteFormatted}`;
      const displayTime = `${hour % 12 || 12}:${minuteFormatted} ${hour < 12 ? 'AM' : 'PM'}`;
      options.push({ value: time, label: displayTime });
    }
  }
  return options;
};

const timeOptions = generateTimeOptions();

const formatTimeDisplay = (time: string) => {
  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours, 10);
  return `${hour % 12 || 12}:${minutes} ${hour < 12 ? 'AM' : 'PM'}`;
};

const days = [
  { id: 'sunday', label: 'Sunday' },
  { id: 'monday', label: 'Monday' },
  { id: 'tuesday', label: 'Tuesday' },
  { id: 'wednesday', label: 'Wednesday' },
  { id: 'thursday', label: 'Thursday' },
  { id: 'friday', label: 'Friday' },
  { id: 'saturday', label: 'Saturday' },
];

const OperatingHoursPage = () => {
  const { locationId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<PickupLocationType>(PickupLocationType.Home);
  const [expandedDay, setExpandedDay] = useState<string | null>(null);
  const [selectedDaysToApply, setSelectedDaysToApply] = useState<string[]>([]);
  const [alwaysAvailable, setAlwaysAvailable] = useState(false);
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);
  const [showCreateOperatingHoursForm, setShowCreateOperatingHoursForm] = useState(false);
  const [hoursData, setHoursData] = useState<any>({
    sunday: {
      available: true,
      alwaysAvailable: false,
      start: '09:00',
      end: '17:00',
    },
    monday: {
      available: true,
      alwaysAvailable: false,
      start: '08:00',
      end: '18:00',
    },
    tuesday: {
      available: true,
      alwaysAvailable: false,
      start: '08:00',
      end: '18:00',
    },
    wednesday: {
      available: true,
      alwaysAvailable: false,
      start: '08:00',
      end: '18:00',
    },
    thursday: {
      available: true,
      alwaysAvailable: false,
      start: '08:00',
      end: '18:00',
    },
    friday: {
      available: true,
      alwaysAvailable: false,
      start: '08:00',
      end: '18:00',
    },
    saturday: {
      available: true,
      alwaysAvailable: false,
      start: '09:00',
      end: '17:00',
    },
  });

  const { loading, error, data } = useGetOperatingHoursByLocationAndTypeQuery({
    variables: {
      locationId: parseInt(locationId || '0', 10),
      PickupLocationType: activeTab,
    },
    skip: !locationId,
    onCompleted: data => {
      if (data?.operatingHoursByLocationAndType) {
        const hours = data.operatingHoursByLocationAndType;
        setAlwaysAvailable(hours.alwaysAvailable);
        setHoursData({
          sunday: {
            available: hours.sundayAvailable,
            alwaysAvailable: hours.sundayAlwaysAvailable,
            start: hours.sundayStart,
            end: hours.sundayEnd,
          },
          monday: {
            available: hours.mondayAvailable,
            alwaysAvailable: hours.mondayAlwaysAvailable,
            start: hours.mondayStart,
            end: hours.mondayEnd,
          },
          tuesday: {
            available: hours.tuesdayAvailable,
            alwaysAvailable: hours.tuesdayAlwaysAvailable,
            start: hours.tuesdayStart,
            end: hours.tuesdayEnd,
          },
          wednesday: {
            available: hours.wednesdayAvailable,
            alwaysAvailable: hours.wednesdayAlwaysAvailable,
            start: hours.wednesdayStart,
            end: hours.wednesdayEnd,
          },
          thursday: {
            available: hours.thursdayAvailable,
            alwaysAvailable: hours.thursdayAlwaysAvailable,
            start: hours.thursdayStart,
            end: hours.thursdayEnd,
          },
          friday: {
            available: hours.fridayAvailable,
            alwaysAvailable: hours.fridayAlwaysAvailable,
            start: hours.fridayStart,
            end: hours.fridayEnd,
          },
          saturday: {
            available: hours.saturdayAvailable,
            alwaysAvailable: hours.saturdayAlwaysAvailable,
            start: hours.saturdayStart,
            end: hours.saturdayEnd,
          },
        });
      }
    },
  });

  const [createOperatingHours, { loading: createLoading }] = useCreateOperatingHoursMutation({
    refetchQueries: [
      {
        query: GetOperatingHoursByLocationAndTypeDocument,
        variables: {
          locationId: parseInt(locationId || '0', 10),
          PickupLocationType: activeTab,
        },
      },
    ],
    onCompleted: data => {
      toast.success('Operating hours saved successfully');
      setShowSuccessBanner(true);
      setTimeout(() => setShowSuccessBanner(false), 5000);
      setShowCreateOperatingHoursForm(false);
    },
    onError: error => {
      toast.error(`Error saving operating hours: ${error.message}`);
      setShowCreateOperatingHoursForm(false);
    },
  });

  const [updateOperatingHours, { loading: updateLoading }] = useUpdateOperatingHoursMutation({
    refetchQueries: [
      {
        query: GetOperatingHoursByLocationAndTypeDocument,
        variables: {
          locationId: parseInt(locationId || '0', 10),
          PickupLocationType: activeTab,
        },
      },
    ],
    onCompleted: () => {
      toast.success('Operating hours updated successfully');
      setShowSuccessBanner(true);
      setTimeout(() => setShowSuccessBanner(false), 5000);
      setShowCreateOperatingHoursForm(false);
    },
    onError: (error: any) => {
      toast.error(`Error updating operating hours: ${error.message}`);
      setShowCreateOperatingHoursForm(false);
    },
  });

  useEffect(() => {
    // Reset expanded day when changing tabs
    setExpandedDay(null);
    setSelectedDaysToApply([]);
  }, [activeTab]);

  const handleDayClick = (dayId: string) => {
    if (expandedDay === dayId) {
      setExpandedDay(null);
    } else {
      setExpandedDay(dayId);
      setSelectedDaysToApply([]);
    }
  };

  const handleDayToggle = (dayId: string, isAvailable: boolean) => {
    setHoursData((prev: any) => ({
      ...prev,
      [dayId]: {
        ...prev[dayId],
        available: isAvailable,
      },
    }));
  };

  const handleAlwaysAvailableToggle = (dayId: string, isAlwaysAvailable: boolean) => {
    setHoursData((prev: any) => ({
      ...prev,
      [dayId]: {
        ...prev[dayId],
        alwaysAvailable: isAlwaysAvailable,
      },
    }));
  };

  const handleTimeChange = (dayId: string, field: string, value: string) => {
    setHoursData((prev: any) => ({
      ...prev,
      [dayId]: {
        ...prev[dayId],
        [field]: value,
      },
    }));
  };

  const handleDaySelection = (dayId: string) => {
    setSelectedDaysToApply(prev => {
      if (prev.includes(dayId)) {
        return prev.filter(id => id !== dayId);
      } else {
        return [...prev, dayId];
      }
    });
  };

  const applyCurrentDayToSelected = () => {
    if (!expandedDay) return;

    const currentDaySettings = hoursData[expandedDay];
    const updatedHoursData = { ...hoursData };

    selectedDaysToApply.forEach(dayId => {
      updatedHoursData[dayId] = {
        ...updatedHoursData[dayId],
        available: currentDaySettings.available,
        alwaysAvailable: currentDaySettings.alwaysAvailable,
        start: currentDaySettings.start,
        end: currentDaySettings.end,
      };
    });

    setHoursData(updatedHoursData);
    setSelectedDaysToApply([]);
    toast.success('Hours applied to selected days');
  };

  const handleSave = () => {
    const operatingHoursInput = {
      locationId: parseInt(locationId || '0', 10),
      PickupLocationType: activeTab,
      alwaysAvailable,
      sundayAvailable: hoursData.sunday.available,
      sundayAlwaysAvailable: hoursData.sunday.alwaysAvailable,
      sundayStart: hoursData.sunday.start,
      sundayEnd: hoursData.sunday.end,
      mondayAvailable: hoursData.monday.available,
      mondayAlwaysAvailable: hoursData.monday.alwaysAvailable,
      mondayStart: hoursData.monday.start,
      mondayEnd: hoursData.monday.end,
      tuesdayAvailable: hoursData.tuesday.available,
      tuesdayAlwaysAvailable: hoursData.tuesday.alwaysAvailable,
      tuesdayStart: hoursData.tuesday.start,
      tuesdayEnd: hoursData.tuesday.end,
      wednesdayAvailable: hoursData.wednesday.available,
      wednesdayAlwaysAvailable: hoursData.wednesday.alwaysAvailable,
      wednesdayStart: hoursData.wednesday.start,
      wednesdayEnd: hoursData.wednesday.end,
      thursdayAvailable: hoursData.thursday.available,
      thursdayAlwaysAvailable: hoursData.thursday.alwaysAvailable,
      thursdayStart: hoursData.thursday.start,
      thursdayEnd: hoursData.thursday.end,
      fridayAvailable: hoursData.friday.available,
      fridayAlwaysAvailable: hoursData.friday.alwaysAvailable,
      fridayStart: hoursData.friday.start,
      fridayEnd: hoursData.friday.end,
      saturdayAvailable: hoursData.saturday.available,
      saturdayAlwaysAvailable: hoursData.saturday.alwaysAvailable,
      saturdayStart: hoursData.saturday.start,
      saturdayEnd: hoursData.saturday.end,
    };

    if (data?.operatingHoursByLocationAndType?.id) {
      // Update existing hours
      updateOperatingHours({
        variables: {
          updateOperatingHoursInput: {
            id: data.operatingHoursByLocationAndType.id,
            ...operatingHoursInput,
          },
        },
      });
    } else {
      // Create new hours
      createOperatingHours({
        variables: {
          createOperatingHoursInput: operatingHoursInput,
        },
      });
    }
  };

  // Render the current operating hours
  const renderCurrentHours = () => {
    return <CurrentHoursList hours={data?.operatingHoursByLocationAndType} days={days} />;
  };

  if (loading) {
    return (
      <LoadingContainer>
        <LoadingSpinner />
      </LoadingContainer>
    );
  }

  if (
    error &&
    error.graphQLErrors?.[0]?.extensions?.code !== ErrorCode.OperatingHoursNotYetCreated &&
    data?.operatingHoursByLocationAndType === null
  ) {
    return (
      <Container>
        <ErrorContainer>Error loading operating hours: {error.message}</ErrorContainer>
        <ButtonContainer>
          <CancelButton onClick={() => navigate(-1)}>
            <TimesIcon icon={faTimes} />
            Go Back
          </CancelButton>
        </ButtonContainer>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>Pickup & Return Hours</Title>
        <CloseButton onClick={() => navigate(-1)}>
          <FontAwesomeIcon icon={faTimes} />
        </CloseButton>
      </Header>

      {showSuccessBanner && (
        <SuccessBanner>
          <span>Operating hours saved successfully!</span>
          <CloseButton onClick={() => setShowSuccessBanner(false)}>
            <FontAwesomeIcon icon={faTimes} />
          </CloseButton>
        </SuccessBanner>
      )}

      <Description>Please note that the hours you set apply to all of your vehicles.</Description>

      <CurrentHoursContainer>
        <CurrentHoursHeader>
          <CurrentHoursTitle>
            <ClockIcon icon={faClock} />
            Current Operating Hours
          </CurrentHoursTitle>
        </CurrentHoursHeader>
        {renderCurrentHours()}
      </CurrentHoursContainer>

      <CreateOperatingHoursButtonContainer>
        <OperatingHoursFormModal
          isOpen={showCreateOperatingHoursForm}
          onClose={() => setShowCreateOperatingHoursForm(false)}
          hoursData={hoursData}
          expandedDay={expandedDay}
          selectedDaysToApply={selectedDaysToApply}
          alwaysAvailable={alwaysAvailable}
          createLoading={createLoading}
          updateLoading={updateLoading}
          handleDayClick={handleDayClick}
          handleDayToggle={handleDayToggle}
          handleAlwaysAvailableToggle={handleAlwaysAvailableToggle}
          handleTimeChange={handleTimeChange}
          handleDaySelection={handleDaySelection}
          applyCurrentDayToSelected={applyCurrentDayToSelected}
          setAlwaysAvailable={setAlwaysAvailable}
          handleSave={handleSave}
          onNavigateBack={() => navigate(-1)}
        />
        <Button
          onClick={() => setShowCreateOperatingHoursForm(true)}
          data-testid="operating-hours-edit-button"
        >
          {data?.operatingHoursByLocationAndType?.id
            ? 'Edit operating hours'
            : 'Setup operating hours'}
        </Button>
      </CreateOperatingHoursButtonContainer>
    </Container>
  );
};

export default OperatingHoursPage;
